import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { coursesAPI } from '../../services/api'
import type { CourseDiscussion } from '../../types'
import { courseProblems } from './courseUtils'
import ReportButton from './ReportButton'
import { apiErrorMessage } from '../../utils/apiError'

const errorText = (err: unknown, fallback: string) => {
  const problems = courseProblems(err)
  return problems ? problems.map((p) => p.message).join(' ') : apiErrorMessage(err, fallback)
}

const Author = ({ name, instructor }: { name: string | null; instructor: boolean }) => (
  <span className="text-xs text-gray-500">
    {name ? `@${name}` : 'A former student'}
    {instructor && <span className="ml-1 rounded bg-sky-900/50 px-1 text-sky-200">Instructor</span>}
  </span>
)

/** One thread: its post, replies, and a reply box (unless locked, for students). */
const Thread = ({
  thread,
  manages,
  onChange,
}: {
  thread: CourseDiscussion
  manages: boolean
  onChange: () => void
}) => {
  const [reply, setReply] = useState('')
  const [error, setError] = useState<string | null>(null)

  const run = async (action: () => Promise<unknown>, fallback: string) => {
    setError(null)
    try {
      await action()
      onChange()
    } catch (err) {
      setError(errorText(err, fallback))
    }
  }

  const send = (e: FormEvent) => {
    e.preventDefault()
    run(async () => {
      await coursesAPI.reply(thread.id, reply.trim())
      setReply('')
    }, "Couldn't post the reply.")
  }

  return (
    <li className="rounded-xl border border-terminal-border bg-terminal-surface p-4 text-sm" data-testid="discussion">
      <p className="flex flex-wrap items-center gap-2">
        {thread.is_pinned && <span className="text-xs text-amber-300">Pinned</span>}
        <span className="font-medium text-gray-100">{thread.title}</span>
        <Author name={thread.author_username} instructor={thread.is_instructor} />
        {thread.is_locked && <span className="text-xs text-gray-500">· Locked</span>}
        <span className="ml-auto flex gap-3 text-xs text-gray-400">
          {manages && (
            <>
              <button type="button" onClick={() => run(() => coursesAPI.changeDiscussion(thread.id, { is_pinned: !thread.is_pinned }), "Couldn't change it.")}>
                {thread.is_pinned ? 'Unpin' : 'Pin'}
              </button>
              <button type="button" onClick={() => run(() => coursesAPI.changeDiscussion(thread.id, { is_locked: !thread.is_locked }), "Couldn't change it.")}>
                {thread.is_locked ? 'Unlock' : 'Lock'}
              </button>
            </>
          )}
          {thread.can_remove ? (
            <button type="button" onClick={() => run(() => coursesAPI.removeDiscussion(thread.id), "Couldn't remove it.")}>
              Remove
            </button>
          ) : (
            <ReportButton itemType="discussion" itemId={thread.id} />
          )}
        </span>
      </p>
      <p className="mt-1 whitespace-pre-line text-gray-300">{thread.content}</p>
      {thread.replies.length > 0 && (
        <ul className="mt-3 space-y-2 border-l border-terminal-border pl-3">
          {thread.replies.map((r) => (
            <li key={r.id} data-testid="discussion-reply">
              <p className="flex items-center gap-2">
                <Author name={r.author_username} instructor={r.is_instructor} />
                {r.can_remove ? (
                  <button
                    type="button"
                    onClick={() => run(() => coursesAPI.removeReply(r.id), "Couldn't remove it.")}
                    className="text-xs text-gray-400 hover:underline"
                  >
                    Remove
                  </button>
                ) : (
                  <ReportButton itemType="reply" itemId={r.id} />
                )}
              </p>
              <p className="whitespace-pre-line text-gray-300">{r.content}</p>
            </li>
          ))}
        </ul>
      )}
      {(!thread.is_locked || manages) && (
        <form onSubmit={send} className="mt-3 flex gap-2">
          <input
            aria-label={`Reply to ${thread.title}`}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            maxLength={5000}
            placeholder="Reply"
            className="min-w-0 flex-1 rounded-md border border-terminal-border bg-terminal-bg px-3 py-1.5 text-sm text-gray-100"
          />
          <button
            type="submit"
            disabled={!reply.trim()}
            className="rounded-md border border-terminal-border px-3 py-1.5 text-sm text-gray-100 disabled:opacity-50"
          >
            Reply
          </button>
        </form>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-amber-300">
          {error}
        </p>
      )}
    </li>
  )
}

/**
 * The course's discussions, for its students and instructor. The
 * instructor pins, locks and removes; anyone removes their own posts.
 */
const CourseDiscussions = ({ courseId, manages }: { courseId: number; manages: boolean }) => {
  const [threads, setThreads] = useState<CourseDiscussion[] | null>(null)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [denied, setDenied] = useState(false)

  const load = useCallback(
    () =>
      coursesAPI
        .discussions(courseId)
        .then(setThreads)
        .catch((err) => {
          // Not (or no longer) the course's student: no board at all.
          if ((err as { response?: { status?: number } })?.response?.status === 403) setDenied(true)
          else setThreads([])
        }),
    [courseId]
  )
  useEffect(() => {
    load()
  }, [load])

  const start = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      await coursesAPI.startDiscussion(courseId, { title: title.trim(), content: content.trim() })
      setTitle('')
      setContent('')
      await load()
    } catch (err) {
      setError(errorText(err, "Couldn't start the discussion."))
    }
  }

  if (denied) return null
  return (
    <section aria-labelledby="course-discussions" data-testid="course-discussions">
      <h2 id="course-discussions" className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
        Discussion
      </h2>
      <form onSubmit={start} className="mb-4 space-y-2 rounded-xl border border-terminal-border bg-terminal-surface p-4" aria-label="New discussion">
        <input
          aria-label="Discussion title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="Ask the class or the instructor"
          className="w-full rounded-md border border-terminal-border bg-terminal-bg px-3 py-1.5 text-sm text-gray-100"
        />
        <textarea
          aria-label="Discussion text"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          maxLength={5000}
          rows={2}
          className="w-full rounded-md border border-terminal-border bg-terminal-bg px-3 py-2 text-sm text-gray-100"
        />
        <button
          type="submit"
          disabled={!title.trim() || !content.trim()}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          Post
        </button>
        {error && (
          <p role="alert" className="text-sm text-amber-300">
            {error}
          </p>
        )}
      </form>
      {threads && threads.length === 0 && <p className="text-sm text-gray-400">No discussions yet.</p>}
      {threads && threads.length > 0 && (
        <ul className="space-y-3">
          {threads.map((thread) => (
            <Thread key={thread.id} thread={thread} manages={manages} onChange={load} />
          ))}
        </ul>
      )}
    </section>
  )
}

export default CourseDiscussions
